import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

/**
 * Compose Tailwind class names with automatic conflict resolution.
 * Drop-in for the shadcn-style `cn()` helper.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Extract the readable error message from an axios-style failure. Every
 * e404 Lambda returns `{ error: "<message>" }` (occasionally `{ message }`)
 * on 4xx/5xx; without this we'd surface axios's useless fallback string
 * (`"Request failed with status code 429"`) and the user has no idea what
 * they can do next. Falls through to `err.message` (usually still axios
 * garbage but occasionally something real, e.g. a network-level failure),
 * then null so the caller can supply its own fallback copy.
 */
export function extractApiError(err: unknown): string | null {
  const data = (err as { response?: { data?: { error?: string; message?: string } } } | null)
    ?.response?.data
  return (
    data?.error ??
    data?.message ??
    (err as { message?: string } | null)?.message ??
    null
  )
}

/**
 * Return `url` only if it's a safe absolute http(s) link, else `undefined`.
 *
 * React does NOT sanitise `href`, so rendering a user-supplied URL directly
 * (`<a href={event.streamUrl}>`) lets a stored `javascript:` / `data:` URL run
 * on click — stored XSS (security review F4, 2026-08-16). The backend now
 * rejects non-http(s) URLs on write, so this is belt-and-braces on read: it
 * also covers rows created before that gate and any field the API might expose.
 * Pass the result straight to `href` — `undefined` renders a non-navigating
 * link rather than a dangerous one.
 */
export function safeHref(url: string | null | undefined): string | undefined {
  if (!url) return undefined
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return undefined
  }
  return parsed.protocol === 'http:' || parsed.protocol === 'https:'
    ? url
    : undefined
}
